import { useEffect } from 'react';

const DEFAULT_MARKETING_HEADER_HEIGHT = 64;
export const MARKETING_HEADER_HEIGHT_VAR = '--marketing-header-h';

function toRoundedPx(value, fallback = DEFAULT_MARKETING_HEADER_HEIGHT) {
  const cast = Number(value);
  const safeValue = Number.isFinite(cast) ? cast : fallback;
  return `${Math.max(0, Math.round(safeValue))}px`;
}

export function setMarketingHeaderHeight(height, rootStyle) {
  if (!rootStyle || typeof rootStyle.setProperty !== 'function') {
    return false;
  }

  const nextValue = toRoundedPx(height);
  const currentValue = typeof rootStyle.getPropertyValue === 'function'
    ? rootStyle.getPropertyValue(MARKETING_HEADER_HEIGHT_VAR).trim()
    : '';

  if (currentValue === nextValue) {
    return false;
  }

  rootStyle.setProperty(MARKETING_HEADER_HEIGHT_VAR, nextValue);
  return true;
}

function readResizeEntryHeight(entry) {
  const borderBoxSize = Array.isArray(entry?.borderBoxSize)
    ? entry.borderBoxSize[0]
    : entry?.borderBoxSize;
  if (Number.isFinite(borderBoxSize?.blockSize)) {
    return borderBoxSize.blockSize;
  }

  if (Number.isFinite(entry?.contentRect?.height)) {
    return entry.contentRect.height;
  }

  return null;
}

export function readHeaderHeight(element, entry = null) {
  const observedHeight = readResizeEntryHeight(entry);
  if (Number.isFinite(observedHeight)) {
    return observedHeight;
  }

  if (element && typeof element.getBoundingClientRect === 'function') {
    return element.getBoundingClientRect().height;
  }

  return DEFAULT_MARKETING_HEADER_HEIGHT;
}

export function observeMarketingHeaderHeight({
  element,
  rootElement = typeof document === 'undefined' ? null : document.documentElement,
  ResizeObserverImpl = typeof window === 'undefined' ? null : window.ResizeObserver,
  requestAnimationFrameImpl = typeof window === 'undefined'
    ? (callback) => {
        callback();
        return 0;
      }
    : window.requestAnimationFrame.bind(window),
  cancelAnimationFrameImpl = typeof window === 'undefined'
    ? () => {}
    : window.cancelAnimationFrame.bind(window),
} = {}) {
  if (!element || !rootElement?.style) {
    return () => {};
  }

  const rootStyle = rootElement.style;
  let frameId = 0;
  let queuedHeight = null;

  const flushHeight = (height) => {
    setMarketingHeaderHeight(height, rootStyle);
  };

  const scheduleHeight = (height) => {
    queuedHeight = height;
    if (frameId) {
      return;
    }

    frameId = requestAnimationFrameImpl(() => {
      frameId = 0;
      if (queuedHeight === null) {
        return;
      }

      flushHeight(queuedHeight);
      queuedHeight = null;
    });
  };

  flushHeight(readHeaderHeight(element));

  if (typeof ResizeObserverImpl !== 'function') {
    return () => {
      if (frameId) {
        cancelAnimationFrameImpl(frameId);
      }
    };
  }

  const observer = new ResizeObserverImpl((entries = []) => {
    const entry = entries[0] ?? null;
    scheduleHeight(readHeaderHeight(element, entry));
  });

  observer.observe(element);

  return () => {
    if (frameId) {
      cancelAnimationFrameImpl(frameId);
      frameId = 0;
    }

    observer.disconnect();
  };
}

export function useMarketingHeaderHeightVar(headerRef) {
  useEffect(() => {
    if (typeof document === 'undefined') {
      return undefined;
    }

    const headerElement = headerRef?.current;
    if (!headerElement) {
      return undefined;
    }

    return observeMarketingHeaderHeight({ element: headerElement });
  }, [headerRef]);
}
